import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { HourlyWeather } from '../models/WeatherData';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { convertWindSpeed, getWindSpeedUnit } from '../utils/unitConversion';

interface WindSimulatorProps {
  hourlyWeather: HourlyWeather[];
  selectedDate: string;
  coordinates: { lat: number; lng: number };
}

export const WindSimulator: React.FC<WindSimulatorProps> = ({
  hourlyWeather,
  selectedDate,
  coordinates
}) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();
  const [currentHourIndex, setCurrentHourIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000); // ms per frame
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const arrowLayerRef = useRef<L.LayerGroup | null>(null);

  // Use ALL available hourly weather data (typically includes multiple days)
  // The API usually provides forecast data for multiple days
  const filteredWeather = hourlyWeather.filter(w => w.time);

  console.log('Wind Simulator - Weather data:', {
    selectedDate,
    totalHours: filteredWeather.length,
    totalDays: (filteredWeather.length / 24).toFixed(1),
    firstHour: filteredWeather[0]?.time,
    lastHour: filteredWeather[filteredWeather.length - 1]?.time
  });

  // Find the index closest to selected date to start animation from there
  const selectedDateStart = new Date(selectedDate + 'T00:00:00');
  const defaultStartIndex = filteredWeather.findIndex(w => {
    if (!w.time) return false;
    const wDate = new Date(w.time);
    return wDate.toDateString() === selectedDateStart.toDateString();
  });

  // Initialize to selected date's first hour or 0 if not found
  useEffect(() => {
    if (defaultStartIndex >= 0) {
      setCurrentHourIndex(defaultStartIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultStartIndex, selectedDate]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [coordinates.lat, coordinates.lng],
      zoom: 12,
      zoomControl: false, // Remove zoom controls
      scrollWheelZoom: false, // Disable scroll zoom
      doubleClickZoom: false, // Disable double click zoom
      touchZoom: false, // Disable touch zoom
      dragging: false, // Disable dragging
      attributionControl: false
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    // Add marker for location
    L.marker([coordinates.lat, coordinates.lng], {
      icon: L.divIcon({
        className: 'custom-marker',
        html: '<div style="background: #ef4444; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      })
    }).addTo(map);

    // Create arrow layer
    const arrowLayer = L.layerGroup().addTo(map);
    arrowLayerRef.current = arrowLayer;
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [coordinates]);

  // Update wind arrows on map
  useEffect(() => {
    if (!mapInstanceRef.current || !arrowLayerRef.current || filteredWeather.length === 0) return;

    const map = mapInstanceRef.current;
    const arrowLayer = arrowLayerRef.current;
    arrowLayer.clearLayers();

    const currentWeather = filteredWeather[currentHourIndex];
    const windDirection = currentWeather.windDirection || 0;
    const windSpeed = currentWeather.windSpeed || 0;

    // Get wind color based on strength
    const getWindColor = (speed: number): string => {
      if (speed < 2) return '#60a5fa'; // Light blue
      if (speed < 6) return '#3b82f6'; // Blue
      if (speed < 11) return '#2563eb'; // Dark blue
      if (speed < 17) return '#f59e0b'; // Orange
      return '#ef4444'; // Red
    };

    const color = getWindColor(windSpeed);
    const bounds = map.getBounds();

    // Create grid of arrows - more arrows for better visibility
    const gridSize = 6; // 6x6 grid = 36 arrows
    const latStep = (bounds.getNorth() - bounds.getSouth()) / (gridSize + 1);
    const lngStep = (bounds.getEast() - bounds.getWest()) / (gridSize + 1);

    for (let i = 1; i <= gridSize; i++) {
      for (let j = 1; j <= gridSize; j++) {
        const lat = bounds.getSouth() + latStep * i;
        const lng = bounds.getWest() + lngStep * j;
        
        // More reasonable arrow sizes
        const baseSize = 30; // Base size reduced from 50 to 30
        const arrowSize = Math.min(Math.max(windSpeed * 3 + baseSize, baseSize), 60); // Max 60 instead of 100
        const opacity = Math.min(Math.max(windSpeed / 15, 0.7), 1);

        const arrow = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'wind-arrow-marker',
            html: `
              <div style="
                width: ${arrowSize}px; 
                height: ${arrowSize}px; 
                display: flex; 
                align-items: center; 
                justify-content: center;
                filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
              ">
                <svg width="100%" height="100%" viewBox="0 0 24 24" style="transform: rotate(${windDirection}deg); transition: transform 0.5s ease-out;">
                  <defs>
                    <filter id="glow-${i}-${j}">
                      <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  <path
                    d="M12 3 L12 17 M12 17 L7 12 M12 17 L17 12"
                    fill="none"
                    stroke="${color}"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    opacity="${opacity}"
                    filter="url(#glow-${i}-${j})"
                  />
                </svg>
              </div>
            `,
            iconSize: [arrowSize, arrowSize],
            iconAnchor: [arrowSize / 2, arrowSize / 2]
          })
        });

        arrow.addTo(arrowLayer);
      }
    }
  }, [currentHourIndex, filteredWeather]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying || filteredWeather.length === 0) return;

    const interval = setInterval(() => {
      setCurrentHourIndex(prev => {
        if (prev >= filteredWeather.length - 1) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [isPlaying, speed, filteredWeather.length]);

  if (filteredWeather.length === 0) {
    return null;
  }

  const currentWeather = filteredWeather[currentHourIndex];
  const currentTime = currentWeather.time ? new Date(currentWeather.time) : new Date();
  
  // Get wind direction in degrees and convert to rotation
  const windDirection = currentWeather.windDirection || 0;
  const windSpeed = currentWeather.windSpeed || 0;
  const windGust = currentWeather.windGust || windSpeed;

  // Calculate wind strength category for visual intensity
  const getWindStrength = (speed: number): string => {
    if (speed < 2) return 'calm';
    if (speed < 6) return 'light';
    if (speed < 11) return 'moderate';
    if (speed < 17) return 'strong';
    return 'gale';
  };

  const windStrength = getWindStrength(windSpeed);

  // Get wind direction text
  const getWindDirectionText = (deg: number): string => {
    const dirs = [t.north, t.northeast, t.east, t.southeast, t.south, t.southwest, t.west, t.northwest];
    const index = Math.round(deg / 45) % 8;
    return dirs[index];
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setCurrentHourIndex(0);
    setIsPlaying(false);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentHourIndex(parseInt(e.target.value));
    setIsPlaying(false);
  };

  const handleSpeedChange = (newSpeed: number) => {
    setSpeed(newSpeed);
  };

  // Get background color based on wind strength
  const getWindBackgroundColor = (speed: number): string => {
    if (speed < 2) return 'from-sky-950/50 to-blue-950/50';
    if (speed < 6) return 'from-blue-900/50 to-blue-950/50';
    if (speed < 11) return 'from-blue-800/50 to-blue-900/50';
    if (speed < 17) return 'from-orange-900/50 to-blue-900/50';
    return 'from-red-900/50 to-orange-900/50';
  };

  return (
    <div id="wind-simulator-section" className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-900/20 to-cyan-900/20 backdrop-blur-xl border border-blue-400/20 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-row items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🌬️</span>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white">
              {t.windSimulator}
            </h3>
            <p className="text-xs text-slate-400">
              {filteredWeather.length} {t.hours} • {(filteredWeather.length / 24).toFixed(1)} {t.days}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-sm font-medium text-white">
              {currentTime.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })}
            </div>
            <div className="text-xs text-slate-400">
              Kl {currentTime.getHours()}:00
            </div>
          </div>
        </div>
      </div>

      {/* Map with wind visualization */}
      <div className={`relative w-full h-64 sm:h-80 bg-gradient-to-b ${getWindBackgroundColor(windSpeed)} rounded-xl overflow-hidden border border-blue-400/10 mb-4 transition-colors duration-700`}>
        <div ref={mapRef} className="absolute inset-0 z-0" />

        {/* Wind info cards */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start gap-2 z-10 pointer-events-none">
          {/* Left: Wind speed */}
          <div className="bg-black/80 backdrop-blur-md rounded-lg px-3 py-2 border border-white/20 shadow-lg">
            <div className="flex items-baseline gap-2">
              <span className="text-white font-bold text-2xl">{convertWindSpeed(windSpeed, unitSystem).toFixed(1)}</span>
              <span className="text-blue-300 text-xs font-medium">{getWindSpeedUnit(unitSystem)}</span>
            </div>
            <div className="text-slate-300 text-xs mt-0.5 flex items-center gap-1">
              <span 
                className="text-blue-400 inline-block transition-transform duration-500" 
                style={{ transform: `rotate(${windDirection + 180}deg)` }}
              >
                ↑
              </span>
              {getWindDirectionText(windDirection)}
            </div>
          </div>

          {/* Right: Wind strength badge */}
          <div className={`backdrop-blur-md rounded-lg px-3 py-2 border shadow-lg ${
            windStrength === 'calm' ? 'bg-blue-500/80 border-blue-300/50' :
            windStrength === 'light' ? 'bg-blue-600/80 border-blue-400/50' :
            windStrength === 'moderate' ? 'bg-yellow-600/80 border-yellow-400/50' :
            windStrength === 'strong' ? 'bg-orange-600/80 border-orange-400/50' :
            'bg-red-600/80 border-red-400/50'
          }`}>
            <div className="text-white font-bold text-sm">{
              windStrength === 'calm' ? `😌 ${t.calm}` :
              windStrength === 'light' ? `🍃 ${t.light}` :
              windStrength === 'moderate' ? `💨 ${t.moderate}` :
              windStrength === 'strong' ? `⚠️ ${t.strong}` :
              `🌪️ ${t.gale}`
            }</div>
            <div className="text-white/80 text-xs mt-0.5">
              {t.windGust} {convertWindSpeed(windGust, unitSystem).toFixed(1)} {getWindSpeedUnit(unitSystem)}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline slider */}
      <div className="mb-4">
        <div className="flex justify-between items-center text-xs text-slate-400 mb-2">
          <span className="font-medium">
            {filteredWeather[0]?.time 
              ? new Date(filteredWeather[0].time).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
              : 'Start'
            }
          </span>
          <span className="text-blue-300 font-medium">
            {t.hour} {currentHourIndex + 1} / {filteredWeather.length}
          </span>
          <span className="font-medium">
            {filteredWeather[filteredWeather.length - 1]?.time
              ? new Date(filteredWeather[filteredWeather.length - 1].time || '').toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
              : 'Slut'
            }
          </span>
        </div>
        <input
          type="range"
          min="0"
          max={filteredWeather.length - 1}
          value={currentHourIndex}
          onChange={handleSliderChange}
          className="w-full h-2 sm:h-3 bg-blue-900/30 rounded-lg appearance-none cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentHourIndex / (filteredWeather.length - 1)) * 100}%, rgba(59, 130, 246, 0.2) ${(currentHourIndex / (filteredWeather.length - 1)) * 100}%, rgba(59, 130, 246, 0.2) 100%)`
          }}
        />
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        {/* Playback controls */}
        <div className="flex gap-2 flex-1 sm:flex-initial">
          <button
            onClick={handlePlayPause}
            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-lg font-semibold transition-all shadow-lg ${
              isPlaying 
                ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-lg">{isPlaying ? '⏸' : '▶'}</span>
              <span>{isPlaying ? t.pause : t.play}</span>
            </div>
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors shadow-lg"
            title={t.reset}
          >
            <span className="text-lg">↺</span>
          </button>
        </div>

        {/* Speed controls */}
        <div className="flex items-center gap-2 flex-1 sm:flex-initial">
          <span className="text-xs text-slate-400 font-medium hidden sm:inline whitespace-nowrap">{t.speed}</span>
          <div className="flex gap-1.5 flex-1 sm:flex-initial">
            {[
              { label: '0.5×', value: 2000 },
              { label: '1×', value: 1000 },
              { label: '2×', value: 500 },
              { label: '4×', value: 250 }
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => handleSpeedChange(value)}
                className={`flex-1 sm:flex-initial px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                  speed === value
                    ? 'bg-blue-500 text-white shadow-lg scale-105'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600 hover:scale-105'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .wind-arrow-marker {
          background: transparent !important;
          border: none !important;
        }
        
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          cursor: pointer;
          border: 2.5px solid white;
          box-shadow: 0 3px 6px rgba(0,0,0,0.3), 0 0 0 3px rgba(59, 130, 246, 0.2);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        @media (min-width: 640px) {
          .slider::-webkit-slider-thumb {
            width: 20px;
            height: 20px;
            border: 3px solid white;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3), 0 0 0 4px rgba(59, 130, 246, 0.2);
          }
        }
        
        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 12px rgba(0,0,0,0.4), 0 0 0 6px rgba(59, 130, 246, 0.3);
        }
        
        .slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          cursor: pointer;
          border: 2.5px solid white;
          box-shadow: 0 3px 6px rgba(0,0,0,0.3), 0 0 0 3px rgba(59, 130, 246, 0.2);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        @media (min-width: 640px) {
          .slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border: 3px solid white;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3), 0 0 0 4px rgba(59, 130, 246, 0.2);
          }
        }
        
        .slider::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 12px rgba(0,0,0,0.4), 0 0 0 6px rgba(59, 130, 246, 0.3);
        }
      `}</style>
    </div>
  );
};
